"use client";

import { useEffect, useRef, useState } from "react";
import { capture } from "@/lib/analytics/events";
import {
  CHANNEL_LABEL,
  channelHref,
  shareUrl,
  type ShareChannel,
  type ShareSurface,
} from "@/lib/share";

const MENU_CHANNELS: ShareChannel[] = ["copy", "linkedin", "x", "whatsapp", "email"];

/**
 * Share this page.
 *
 * The URL is read from `window.location` at click time rather than passed in,
 * so a filtered search shares the filtered search and a job page shares that
 * job, with no placement having to thread the URL through. Every generated link
 * carries UTM parameters, which is what makes "did sharing bring anyone who
 * converts?" answerable instead of arguable.
 *
 * On a device with a native share sheet we use it — it reaches every app the
 * person actually has, which no hand-built menu can match. Everywhere else,
 * a small menu. Both fire the same events, so the funnel does not care which
 * one the visitor got.
 */
export function ShareButton({
  surface,
  title,
  label = "Share",
  className,
  block = false,
  jobId,
}: {
  surface: ShareSurface;
  /** What is being shared — used as the message text on channels that take one. */
  title: string;
  label?: string;
  className?: string;
  /** Full-width variant, for the job page's stacked action column. */
  block?: boolean;
  /**
   * Job id, on the job surface only. Kept because the Control Center's funnel
   * board reads `job_shared` for this product; dropping it while swapping the
   * component would zero a tracked row and look like sharing had stopped.
   * `share_click` is the portfolio-wide event and fires on every surface.
   */
  jobId?: number;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // A menu that only closes via its own button is a menu people leave open and
  // then click through by accident.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const linkFor = (channel: ShareChannel) =>
    shareUrl(window.location.href, surface, channel);

  /** Keeps the pre-existing `job_shared` row alive on the job surface. */
  const captureLegacyJobShare = (channel: ShareChannel) => {
    if (surface !== "job" || jobId === undefined) return;
    if (channel !== "linkedin" && channel !== "x" && channel !== "copy") return;
    capture("job_shared", { job_id: jobId, channel });
  };

  const onChannel = async (channel: ShareChannel) => {
    const url = linkFor(channel);
    capture("share_click", { surface, channel });
    captureLegacyJobShare(channel);
    if (channel === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch {
        /* clipboard blocked (http, or permission denied) — the menu stays open
           so the link can still be selected from the input below. */
      }
      return;
    }
    const href = channelHref(channel, url, title);
    if (href) window.open(href, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const onClick = async () => {
    capture("share_open", { surface });
    // Feature-detect rather than sniff the user agent: desktop Safari has
    // navigator.share too, and it is a perfectly good share sheet there.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, url: linkFor("native") });
        capture("share_click", { surface, channel: "native" });
        captureLegacyJobShare("native");
        return;
      } catch {
        // AbortError = the person closed the sheet. That is not a failure and
        // must not be counted as a share; fall through to the menu only when
        // the API is unavailable, not when it was dismissed.
        return;
      }
    }
    setOpen((v) => !v);
  };

  // shrink-0 in block mode: this sits next to a flex-1 "Save this job" in an
  // 18rem sidebar. Without it the wrapper is sized to content and the w-full
  // button collapses to the label's own min-content width — the border ends up
  // flush against the "S" and the "e", which is what shipped.
  return (
    <div ref={boxRef} className={block ? "relative shrink-0" : "relative inline-block"}>
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Share this page"
        className={
          className ??
          (block
            ? "relative z-10 flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-700 transition-colors hover:border-brand-300 hover:text-brand-700"
            : "relative z-10 inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-semibold text-neutral-500 transition-colors hover:border-neutral-400 hover:text-ink")
        }
      >
        <ShareIcon />
        <span>{copied ? "Link copied" : label}</span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-lg"
        >
          {MENU_CHANNELS.map((c) => (
            <button
              key={c}
              type="button"
              role="menuitem"
              onClick={() => onChannel(c)}
              className="block w-full px-3 py-1.5 text-left text-[13px] text-neutral-700 hover:bg-neutral-50 hover:text-ink"
            >
              {c === "copy" && copied ? "Copied ✓" : CHANNEL_LABEL[c]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden fill="none">
      <path
        d="M11 5.5 8 2.5 5 5.5M8 2.5v8M3.5 9v3.5a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
